import ThreeGlobe from "three-globe";
import countries from "../assets/globe-min.json";
import arcsData from "../assets/arcs-data.json";
import { hexToRgb, genRandomNumbers } from "../systems/utils";
import { Color } from "three";
import { Raycaster, Vector2 } from "three";
import { PerspectiveCamera } from "three";
import { IPA_GLOBE_DEFAULTS } from "../ipa-globe-defaults";

const ARC_REL_LEN = 0.9; // relative to whole arc
const FLIGHT_TIME = 2000;
const NUM_RINGS = 1;
const RINGS_MAX_R = 3; // deg
const RING_PROPAGATION_SPEED = 3; // deg/sec

const interval = 2;

class Globe {
  constructor(options = {}) {
    this._ringTick = 0;
    this.opt = { ...IPA_GLOBE_DEFAULTS, ...options };
    this.instance = new ThreeGlobe({
      waitForGlobeReady: true,
      animateIn: true,
    });
    this.pointsData = [];

    this._buildData();
    this._buildMaterial();

    this.camera = new PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 5;
    this.camera.lookAt(this.instance.position);

    this.raycaster = new Raycaster();
    this.mouse = new Vector2();
    this._pickingCamera = null;
    this._pickingCanvas = null;
    this._canvasClickHandler = (event) => this._onCanvasClick(event);

    this.instance.tick = (delta) => this.tick(delta);
  }

  /**
   * Listen on the canvas only. A window-level listener with preventDefault()
   * breaks normal link and button clicks across the whole page.
   */
  attachToCanvas(canvas, sceneCamera) {
    if (this._pickingCanvas) {
      this._pickingCanvas.removeEventListener("click", this._canvasClickHandler, false);
    }
    this._pickingCamera = sceneCamera;
    this._pickingCanvas = canvas;
    canvas.addEventListener("click", this._canvasClickHandler, false);
  }

  async init() {
    await this._buildData();
    this.initCountries(1000);
    this.initAnimationData(1000);
  }

  _onCanvasClick(event) {
    const canvas = event.currentTarget;
    const cam = this._pickingCamera;
    if (!cam || !canvas.getBoundingClientRect) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, cam);
    const intersects = this.raycaster.intersectObjects(this.instance.children, true);

    if (intersects.length > 0) {
      const pointData = intersects[0].object.userData;
      this._onPointClick(pointData);
    }
  }

  _onPointClick() {
    /* Hook for future interactions */
  }

  initCountries(delay) {
    const o = this.opt;
    setTimeout(() => {
      this.instance
        .hexPolygonsData(countries.features)
        .hexPolygonResolution(3)
        .hexPolygonMargin(0.7)
        .showAtmosphere(!!o.showAtmosphere)
        .atmosphereColor(o.atmosphereColor)
        .atmosphereAltitude(o.atmosphereAltitude)
        .hexPolygonColor(() => o.hexPolygonColor);
    }, delay);
  }

  initAnimationData(delay) {
    const o = this.opt;
    const hubLat = o.hubLat;
    const hubLng = o.hubLng;
    const flights = arcsData.flights.map((f) => ({
      ...f,
      startLat: hubLat,
      startLng: hubLng,
    }));

    setTimeout(() => {
      const hubPoint = {
        lat: hubLat,
        lng: hubLng,
        color: o.hubPointColor,
        radius: o.hubRadius,
        altitude: 0.0,
      };

      const destinationPoints = flights.map((flight) => ({
        lat: flight.endLat,
        lng: flight.endLng,
        color: o.destinationPointColor,
        radius: o.pointRadius,
        altitude: 0.0,
      }));

      this.pointsData = [hubPoint, ...destinationPoints];

      this.instance
        .arcsData(flights)
        .arcStartLat((d) => d.startLat * 1)
        .arcEndLat((d) => d.endLat * 1)
        .arcStartLng((d) => d.startLng * 1)
        .arcEndLng((d) => d.endLng * 1)
        .arcColor(() => o.arcColor)
        .arcAltitude((e) => e.arcAlt * 1)
        .arcStroke(o.arcStroke)
        .arcDashLength(0.4)
        .arcDashInitialGap(0)
        .arcDashGap(0.1)
        .arcDashAnimateTime(o.arcDashAnimateTime)
        .pointsData(this.pointsData)
        .pointColor((e) => e.color)
        .pointsMerge(true)
        .pointAltitude((e) => e.altitude)
        .pointRadius((e) => e.radius)
        .ringsData([])
        .ringColor((e) => (t) => {
          const c = e.color;
          return typeof c === "function" ? c(t) : c;
        })
        .ringMaxRadius(RINGS_MAX_R)
        .ringPropagationSpeed(RING_PROPAGATION_SPEED)
        .ringRepeatPeriod((FLIGHT_TIME * ARC_REL_LEN) / NUM_RINGS);
    }, delay);
  }

  tick(delta) {
    this._ringTick += delta;

    if (this._ringTick > interval) {
      const numbersOfRings = genRandomNumbers(
        0,
        this.pointsData.length,
        Math.floor((this.pointsData.length * 4) / 5)
      );
      this.instance.ringsData(
        this.pointsData.filter((d, i) => numbersOfRings.includes(i))
      );

      this._ringTick = this._ringTick % interval;
    }
  }

  async _buildData() {
    let points = [];
    try {
      // Fetch data from API
      const response = await fetch('https://api.reliefweb.int/v1/disasters?appname=rw-user-0&profile=full&preset=latest&slim=0&limit=1');
      const data = await response.json();
      const arcs = data.data; // get the 'data' array from the response

      for (let i = 0; i < arcs.length; i++) {
        const arc = arcs[i];
        const rgb = hexToRgb('#62DAFF'); // use a fixed color for now
        points.push({
          size: 1.0,
          order: i, // use the index as the order
          color: (t) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`,
          label: arc.fields.primary_country.name,
          lat: arc.fields.primary_country.location.lat,
          lng: arc.fields.primary_country.location.lon,
        });
      }
      this.pointsData = points.filter(
        (v, i, a) =>
          a.findIndex((v2) => ["lat", "lng"].every((k) => v2[k] === v[k])) === i
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }

  _buildMaterial() {
    const o = this.opt;
    const globeMaterial = this.instance.globeMaterial();
    globeMaterial.color = new Color(o.globeColor);
    globeMaterial.emissive = new Color(o.globeEmissive);
    globeMaterial.emissiveIntensity = o.globeEmissiveIntensity;
    globeMaterial.shininess = 0.9;
    globeMaterial.transparent = true;
    globeMaterial.opacity = o.globeOpacity;
  }
}

export { Globe };
